import { Button, makeStyles } from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { EntityColumn } from './types';
import { BOOLEAN_LOOKUP_OPTIONS, DATA_OPS, FilterType, OPERATORS } from './columnFilterOps';
import { readLookupSelection } from './picklistSelection';

// Compact read-only summary of active `filter.<col>.<op>` overrides, with a one-click clear per
// chip — the makeStyles (Fluent v9) rewrite of ptc-core CRM's Tailwind ActiveFiltersChips (Spec 2).
// Discoverability companion to the header-driven filter popovers (ColumnHeaderFilter). Reflects the
// URL filter overrides (`list.filters`), not view-inherited filters.

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '12px',
  },
});

export interface ActiveFiltersChipsProps<TItem> {
  /** Column catalog, for labels + filterType (to resolve the operator's display label). */
  columns: EntityColumn<TItem>[];
  /** The flat filter record (`list.filters`) — one condition per column (v1). */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

/** `filter.<col>.<op>` — col may itself contain dots; the LAST segment is the op. */
const FILTER_KEY_PATTERN = /^filter\.(.+)\.([^.]+)$/;

interface Chip {
  key: string;
  label: string;
}

export function ActiveFiltersChips<TItem,>({ columns, value, onChange }: ActiveFiltersChipsProps<TItem>) {
  const styles = useStyles();

  const chips: Chip[] = Object.entries(value).flatMap(([key, v]) => {
    const m = FILTER_KEY_PATTERN.exec(key);
    if (!m) return [];
    const [, colKey, op] = m;
    const col = columns.find((c) => c.key === colKey);
    const colLabel = col?.label ?? colKey;

    // C86 (fleet filter contract v3 §16.8): a lookup column's `in`/`notIn` chip reads as the
    // column + its selected values, no operator label at all -- "Preparer: Emma, Carlos +1", never
    // "Preparer: Any of \"Emma, Carlos\"". Any OTHER op on a picklist/boolean column (eq/ne/
    // containsData/noData -- reachable only from a legacy saved view or hand-edited URL, never from
    // this UI any more) keeps the old generic format below, unchanged.
    if (col && (col.filterType === 'picklist' || col.filterType === 'boolean') && (op === 'in' || op === 'notIn')) {
      const lookupOptions = col.filterType === 'boolean' ? BOOLEAN_LOOKUP_OPTIONS : col.filterOptions ?? [];
      const sel = readLookupSelection(op, v);
      const labels = [
        ...sel.codes.map((code) => lookupOptions.find((o) => o.value === code)?.label ?? code),
        ...(sel.includeNoValue ? ['Has no value'] : []),
      ];
      const displayList = labels.length > 2
        ? `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
        : labels.join(', ');
      return [{ key, label: `${colLabel}: ${sel.excluding ? 'Not ' : ''}${displayList}` }];
    }

    const ops = col?.filterType ? OPERATORS[col.filterType as FilterType] : undefined;
    const opLabel = ops?.find((o) => o.op === op)?.label ?? op;
    // Map picklist codes back to their display labels for a readable chip.
    const displayValue = (col?.filterOptions && v)
      ? v.split(',').map((code) => col.filterOptions!.find((o) => o.value === code)?.label ?? code).join(', ')
      : v;
    const valueFragment = DATA_OPS.has(op) || v === '' ? '' : ` "${displayValue}"`;
    return [{ key, label: `${colLabel}: ${opLabel}${valueFragment}` }];
  });

  if (chips.length === 0) return null;

  return (
    <div className={styles.root} role="group" aria-label="Active filters">
      {chips.map((chip) => (
        <Button
          key={chip.key}
          size="small"
          shape="circular"
          appearance="secondary"
          icon={<Dismiss12Regular />}
          iconPosition="after"
          aria-label={`Remove ${chip.label} filter`}
          onClick={() => {
            const next = { ...value };
            delete next[chip.key];
            onChange(next);
          }}
        >
          {chip.label}
        </Button>
      ))}
    </div>
  );
}

export default ActiveFiltersChips;
